/**
 * @file PageActions
 * Exports actionCreators and the PageEventActions instance (bottom of the file)
 * @copyright 2015 CBC/Radio-Canada. All rights reserved.
 */
import { throttle } from "lodash-es";
import {
    MEDIA_QUERY_MEDIUM,
    MEDIA_QUERY_SMALL,
    VIEWPORT_DEFAULTS,
    THROTTLES,
} from "./constants";
import log from "../logger/library";
import { isBrowser } from "../environmentDetection/library";

const MEDIA_QUERY_KEY_SMALL = "SMALL";
const MEDIA_QUERY_KEY_MEDIUM = "MEDIUM";

export const VIEWPORT_CLASS_SMALL = "viewportSmall";
export const VIEWPORT_CLASS_MEDIUM = "viewportMedium";
export const VIEWPORT_CLASS_LARGE = "viewportLarge";

/**
 *  metaFlexiblePathReplacer replaces "/meta/flexible" with "" or "/homepage" when appropriate
 * @param {string} path
 * @returns {string}
 */
export const metaFlexiblePathReplacer = (path) => {
    if (path === "/meta/flexible" || path === "/meta/flexible/") {
        return "/homepage";
    }

    return path.replace("/meta/flexible", "");
};

/**
 * metaNinePreviewReplacer replaces "/meta/nine-preview" with "" or "/homepage" when appropriate

 * @param {string} path
 * @returns {string}
 */
export const metaNinePreviewReplacer = (path) => {
    if (path === "/meta/nine-preview" || path === "/meta/nine-preview/") {
        return "/homepage";
    }

    return path.replace("/meta/nine-preview", "");
};
/**
 * metaPreviewReplacer replaces "/meta/preview" with ""

 * @param {string} path
 * @returns {string}
 */
export const metaPreviewReplacer = (path) => {
    return path.replace("/meta/preview", "");
};

/**
 * getIsMetaPreview checks if "/meta/preview" is at index 0

 * @param {string} path
 * @returns {boolean}
 */
export const getIsMetaPreview = (path) => {
    return path.indexOf("/meta/preview") === 0;
};

/**
 * getIsNinePreview checks if "/meta/nine-preview" is at index 0

 * @param {string} path
 * @returns {boolean}
 */
export const getIsNinePreview = (path) => {
    return path.indexOf("/meta/nine-preview") === 0;
};

/**
 * Determines the type of preview (Nine or Polopoly) based on the path

 * @param {string} path
 * @returns {string} type of preview: "nine", "polopoly", "" (none)
 */
export const previewType = (path) => {
    if (getIsNinePreview(path)) {
        return "nine";
    } else if (getIsMetaPreview(path)) {
        return "polopoly";
    } else {
        return "";
    }
};

class PageActions {
    /**
     * @constructor
     */
    constructor() {
        this.eventMap = [
            {
                event: "scroll",
                callback: this.onScrollHandler,
                throttle: THROTTLES.scroll,
            },
            {
                event: "resize",
                callback: this.onResizeHandler,
                throttle: THROTTLES.resize,
            },
        ];

        // Objects that depend on the viewport
        this.viewport = VIEWPORT_CLASS_LARGE;
        this.mediaQueryObjects = new Set();
        this.stickyObjects = new Set();
        this.resizeObjects = new Set();
        this.wallpaperObjects = new Set();

        // MediaQueryListener Map
        this.breakpointListeners = new Map();

        this.previousScrollY = 0;
        this.previousWindowWidth = 0;
    }

    /**
     * @bindEvents
     * @returns {void}
     */
    bindEvents() {
        this.eventMap.forEach((item) => {
            let throttled = throttle(item.callback.bind(this), item.throttle);
            window.addEventListener(item.event, throttled);
        });
    }

    /**
     * @bindMediaQueries
     * @returns {void}
     */
    bindMediaQueries() {
        if (isBrowser()) {
            const mqls = new Map(
                Object.entries({
                    [MEDIA_QUERY_KEY_MEDIUM]: window.matchMedia(
                        `(max-width: ${MEDIA_QUERY_MEDIUM})`
                    ),
                    [MEDIA_QUERY_KEY_SMALL]: window.matchMedia(
                        `(max-width: ${MEDIA_QUERY_SMALL})`
                    ),
                })
            );

            this.breakpointListeners = mqls;
            this.handleBreakpointChange();
            mqls.forEach((mql) => {
                mql?.addEventListener?.("change", () => {
                    this.handleBreakpointChange();
                });
            });
        }
    }

    handleBreakpointChange() {
        if (this.breakpointListeners.get(MEDIA_QUERY_KEY_SMALL).matches) {
            this.viewport = VIEWPORT_CLASS_SMALL;
        } else if (
            this.breakpointListeners.get(MEDIA_QUERY_KEY_MEDIUM).matches
        ) {
            this.viewport = VIEWPORT_CLASS_MEDIUM;
        } else {
            this.viewport = VIEWPORT_CLASS_LARGE;
        }

        // Go over registered objects that depend on media queries and call their callback to update state
        if (this.mediaQueryObjects !== undefined) {
            //&& this.mediaQueryObjects.size) {
            this.mediaQueryObjects.forEach(
                this.callMediaQueryCallback.bind(this)
            );
        }
    }

    callMediaQueryCallback(componentInfo) {
        if (componentInfo.callback !== undefined) {
            componentInfo.callback(this.viewport);
        }
    }

    /**
     * @onScrollHandler
     * Bind any scrolling dependent actions here
     * @returns {void}
     */
    onScrollHandler() {
        try {
            // Only listen to vertical scroll (up/down)
            if (window.scrollY !== this.previousScrollY) {
                this.stickyStateHandler();
            }
        } catch (error) {
            log.error("(PageActions.onScrollHandler)", error);
        }
    }

    /**
     * @onResizeHandler
     * Bind any window resize dependent actions here
     * @returns {void}
     */
    onResizeHandler() {
        if (window.innerWidth !== this.previousWindowWidth) {
            // Only listen to width resize (not height)
            this.viewportDimensionsHandler();
        }
    }

    /**
     * Components can register with PageActions via this method
     * e.g. Featured area is registered with this method to listen to media queries changes
     * @param {object} componentInfo An object containing {domNode, callback} that depends on page events
     * @returns {void}
     */
    registerMediaQueryObject(componentInfo) {
        this.mediaQueryObjects.add(componentInfo);
        this.callMediaQueryCallback(componentInfo);
    }

    /**
     * Unregister components that no longer depend on mediaQueries
     * @param {object} componentInfo An object containing {domNode, callback} that depends on page events
     * @returns {void}
     */
    unregisterMediaQueryObject(componentInfo) {
        this.mediaQueryObjects.delete(componentInfo);
    }

    /**
     * Components can register with PageActions via this method to listen to resize events
     * e.g. Navigations register to hide items that can't fit within the current viewport (show More menu)
     * @param {object} componentInfo An object containing {domNode, callback, boundingRect} that depends on events
     * @returns {void}
     */
    registerResizeObject(componentInfo) {
        this.resizeObjects = this.resizeObjects.add(componentInfo);
    }

    /**
     * Unregister components that no longer depend on page resize
     * @param {object} componentInfo An object containing {domNode, callback, boundingRect} that no longer depends on events
     * @returns {void}
     */
    unregisterResizeObject(componentInfo) {
        this.resizeObjects.delete(componentInfo);
    }

    /**
     * Components dependent on Wallpaper Ads can register with PageActions via this method
     * @param {object} componentInfo An object containing {domNode, callback, boundingRect} that depends on wallpaper resize
     * @returns {void}
     */
    registerWallpaper(componentInfo) {
        this.wallpaperObjects.add(componentInfo);
    }

    /**
     * One of the onScroll handlers that check if registered components should be in
     * sticky state (position:fixed) or not
     * @returns {void}
     */
    stickyStateHandler() {
        let rsContainer = document.querySelector(".ad-risingstar");
        let rsContainerHeight =
            rsContainer !== null ? rsContainer.offsetHeight : 0;

        this.stickyObjects.forEach((componentInfo) => {
            let direction = "";

            // Get the scrolling direction
            if (
                window.scrollY &&
                componentInfo.previousScrollY > window.scrollY &&
                window.scrollY > rsContainerHeight
            ) {
                direction = "scrollUp";
            } else if (
                window.scrollY &&
                componentInfo.previousScrollY < window.scrollY &&
                window.scrollY > rsContainerHeight
            ) {
                direction = "scrollDown";
            }

            // Get new sticky state, based on the scroll
            componentInfo.isSticky = direction.trim() ? true : false;
            componentInfo.previousScrollY = window.scrollY;

            // Update the value for next time the user scrolls
            this.previousScrollY = window.scrollY;

            // Only re-render if the sticky state has changed
            if (
                direction !== "static" &&
                componentInfo.callback !== undefined
            ) {
                // Launch component's callback
                componentInfo.callback(componentInfo.isSticky, direction);
            }
        });
    }

    /**
     * @enableWallpaper
     * @returns {void}
     */
    enableWallpaper() {
        // Update objects that need to be resized
        this.resizeObjects.forEach((componentInfo) => {
            componentInfo.wallpaperWidth = VIEWPORT_DEFAULTS.wallpaperWidth;
        });

        // Update objects dependent on wallpaper
        this.wallpaperObjects.forEach((componentInfo) => {
            if (componentInfo.callback !== undefined) {
                componentInfo.callback(true);
            }
        });
    }

    /**
     * @disableWallpaper
     * @returns {void}
     */
    disableWallpaper() {
        this.resizeObjects.forEach((componentInfo) => {
            componentInfo.wallpaperWidth = 0;
        });

        // Update objects dependent on wallpaper
        this.wallpaperObjects.forEach((componentInfo) => {
            if (componentInfo.callback !== undefined) {
                componentInfo.callback(false);
            }
        });
    }

    /**
     * Pass the viewport dimensions to onResize objects
     * @param {number} overrideWidth used by the Clickable Wallppaer
     * @returns {void}
     */
    viewportDimensionsHandler(overrideWidth = 0) {
        // overrideWidth is needed for wallpaper
        // this ensures width will be different from this.previousWindowWidth
        const width = overrideWidth > 0 ? overrideWidth : window.innerWidth;
        if (width !== this.previousWindowWidth) {
            this.resizeObjects.forEach((componentInfo) => {
                if (componentInfo.callback !== undefined) {
                    componentInfo.callback(width);
                }
            });
        }
        // Update the value for next time the user resizes
        this.previousWindowWidth = window.innerWidth;
    }

    /**
     * Certain actions, like opening the menu or photogalleries, need to prevent the scrolling on the body
     * @param {bool} shouldFreeze Indicator on whether the scrolling should be frozen on body
     * @returns {void}
     */
    updateBodyScrollState(shouldFreeze) {
        if (shouldFreeze) {
            document.body.classList.add("scrollFreeze");
        } else {
            document.body.classList.remove("scrollFreeze");
        }
    }

    /**
     * Calculate the number of items that don't fit inside the domNode
     * @param {object} domNode The domNode object that needs to
     * @param {bool} collapseOnMobile Whether to collapse the whole list on mobile
     * @param {number} containerWidth needed if overrideWidth is provided for Clickable Wallpaper
     * @param {bool} rightNavOffsetWidth true if we are using right side nav
     * @returns {void}
     */
    calculateHiddenItems(
        domNode,
        collapseOnMobile = true,
        containerWidth = 0,
        rightNavOffsetWidth = 0
    ) {
        let hiddenItemsCount = 0;
        // if containerWidth isn't supplied width will be window.innerWidth
        // containerWidth is used to override the width for wallpaper display
        const width = containerWidth > 0 ? containerWidth : window.innerWidth;

        if (
            !domNode ||
            (collapseOnMobile && width <= VIEWPORT_DEFAULTS.mobileWidth)
        ) {
            return hiddenItemsCount;
        }

        const listItems = domNode.childNodes;
        const viewportWidth = Math.min(
            width,
            domNode.offsetWidth,
            VIEWPORT_DEFAULTS.maxWidth
        );
        if (listItems) {
            // The "more" button is visible at this point

            let moreButtonPosition = listItems.length - 1;

            if (rightNavOffsetWidth > 0) {
                moreButtonPosition = moreButtonPosition - 1;
            }

            const moreButton = listItems[moreButtonPosition];

            let overallWidth = rightNavOffsetWidth + moreButton.offsetWidth;

            // Convert node collection to an array and go over all the list items' width and add up their widths,
            // up to the max. Count the number of visible and hidden elements
            const nodeArray = Array.prototype.slice.call(
                domNode.childNodes,
                0,
                moreButtonPosition
            );

            nodeArray.forEach((item) => {
                overallWidth += item.offsetWidth;
                if (viewportWidth <= overallWidth) {
                    hiddenItemsCount++;
                }
            });
        }

        return hiddenItemsCount;
    }
}

const PageActionsInstance = new PageActions();
export default PageActionsInstance;
